import {Component, Object3D, PhysXComponent, Prefab, Property, Scene} from '@wonderlandengine/api';
import Logger from './utils/logger.js';
import {property} from '@wonderlandengine/api/decorators.js';
import {EventsClient} from './events-client.js';
import {vec3, quat} from 'gl-matrix';
import { UserMessaging } from './utils/user-messaging.js';
import { PlayerMovementHandler } from './player-movement-handler.js';

const MODULE = 'SceneController';

/**
 * scene-controller
 */
export class SceneController extends Component {
    static TypeName = 'scene-controller';

    /* Properties that are configurable in the editor */
    @property.array(Property.string())
    sceneRefs: string[] = [];

    private sceneRoot!: Object3D;
    private scenePrefab!: Prefab;
    private _currentSceneRef: string | null = null;
    
    private _playerPhysx: PhysXComponent | null = null;

    // Singleton
    private static _instance: SceneController;
    static get instance(): SceneController {
        return SceneController._instance;
    }

    get playerPhysx(): PhysXComponent | null {
        if (!this._playerPhysx) {
            const eventsClient = EventsClient.instance;
            if (!eventsClient) {
                Logger.error(MODULE, 'EventsClient singleton not available.');
            } else {
                const physxComponent = eventsClient.object.getComponent(PhysXComponent);
                if (!physxComponent) {
                    Logger.error(MODULE, 'Player object does not have a PhysXComponent.');
                } else {
                    this._playerPhysx = physxComponent;
                }
            }
        }

        return this._playerPhysx;
    }

    get currentSceneRef(): string | null {
        return this._currentSceneRef;
    }

    start() {        
        //Initialize singleton
        if (SceneController._instance) {
            Logger.error(MODULE, 'There can only be one instance of SceneController Component');
        } else {
            SceneController._instance = this;
            // Initialize currentSceneRef to first scene by default if available
            if (this.sceneRefs.length > 0) {
                this._currentSceneRef = this.sceneRefs[0];
            }
        }
    }

    preloadScene() {
        if (this.playerPhysx) {
            this.playerPhysx.active = false;
        }
    }

    async loadScene(sceneRef: string | number) {
        let sceneName: string | undefined;

        if (typeof sceneRef === 'number') {
            sceneName = this.sceneRefs[sceneRef];
        } else if (typeof sceneRef === 'string') {
            sceneName = this.sceneRefs.find((ref) => ref === sceneRef);
        }

        if (!sceneName) {
            Logger.error(MODULE, 'Scene reference not found in sceneRefs. Defaulting to first scene in list.');
            sceneName = this.sceneRefs[0];
        }

        if (!sceneName) {
            Logger.error(MODULE, 'No scene available to load. sceneRefs is empty.');
            return;
        }

        if (this.sceneRoot && !this.sceneRoot.isDestroyed) {
            this.sceneRoot.destroy();
        }

        const loadedScene = await this.engine.loadPrefab(sceneName);

        const returnVal = this.engine.scene.instantiate(loadedScene);

        this.sceneRoot = returnVal.root;
        this._currentSceneRef = sceneName;
    }

    postLoadScene() {
        // Look for objecct named "SpawnPoint" in the newly loaded scene, and if it exists
        // move the player there
        const eventsClient = EventsClient.instance; 
        if (eventsClient) {
            const playerObject = eventsClient.object;

            // Clear any contacted platforms from the previous scene
            const playerMovementHandler = playerObject.getComponent(PlayerMovementHandler);
            if (playerMovementHandler) {
                playerMovementHandler.clearContactedPlatforms();
            }

            const spawnPoint = this.sceneRoot.findByName('SpawnPoint', true);
            if (spawnPoint && spawnPoint[0]) {
                const tempVec = vec3.create();
                const tempQuat = quat.create();

                spawnPoint[0].getRotationWorld(tempQuat);
                playerObject.setRotationWorld(tempQuat);
                spawnPoint[0].getPositionWorld(tempVec);
                playerObject.setPositionWorld(tempVec);
            } else {
                playerObject.setPositionWorld([0, 0, 0]);
                playerObject.setRotationWorld([0, 0, 0, 1]);
            }
        }

        if (this.playerPhysx) {
            this.playerPhysx.active = true;
        }
    }

    public async switchToScene(sceneRef: string | number) {
        this.preloadScene();
        await this.loadScene(sceneRef);
        this.postLoadScene();
    }
}
